import { JUDGEMENT_LABEL, JUDGEMENT_TONE, toViewTone } from '@/lib/judgement';
import { cn } from '@/lib/utils';
import type { ProgramJudgement } from '@/types/judgement';

import { StatusTag } from './StatusTag';

interface JudgementBadgeProps {
  judgement: ProgramJudgement;
  /**
   * 서버 표현의 단계 이름·톤(specs/069 — 판정 문구 카탈로그). 주면 이 값을 그린다 — 응답에 표현이 있는 화면은 반드시 넘긴다.
   * 없는 화면(표현을 아직 싣지 않은 응답)만 기본 라벨 맵을 쓴다.
   */
  view?: { label: string; tone: string };
  className?: string;
  /** 클릭 가능한 필터로 동작 — false 면 순수 표시 */
  clickable?: boolean;
  /** clickable 일 때만 — 미선택은 투명도·점선 테두리(색은 유지, 오류색 금지) */
  selected?: boolean;
  onToggle?: (judgement: ProgramJudgement) => void;
}

/**
 * 판정 4분류 배지(specs/024 FR-025b) — 라벨·톤 SSoT 는 `lib/judgement.ts`.
 * 예비·확인 필요는 오류가 아니라 지도사 확인 대상이다. 기본은 표시 전용, `clickable` 이면 토글 필터.
 */
export function JudgementBadge({
  judgement,
  view,
  className,
  clickable = false,
  selected = true,
  onToggle,
}: JudgementBadgeProps) {
  const tag = (
    <StatusTag
      tone={view ? toViewTone(view.tone) : JUDGEMENT_TONE[judgement]}
      className={cn(clickable && !selected && 'border-dashed opacity-40', className)}
    >
      {view ? view.label : JUDGEMENT_LABEL[judgement]}
    </StatusTag>
  );
  if (!clickable) return tag;
  return (
    <button
      type="button"
      aria-pressed={selected}
      className="inline-flex min-h-8 items-center rounded-md focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden"
      onClick={() => onToggle?.(judgement)}
    >
      {tag}
    </button>
  );
}
